import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';

import { fetchHomes, setHome } from '../../actions/homes';
import { fetchTransferts } from '../../actions/transferts';

import Loading from '../common/Loading';
import withDragDropContext from './withDragDropContext';
import ImageDropTarget from './ImageDropTarget';
import ImageContainer, { getImageSrc } from './ImageContainer';
import RenderToast from '../home-status-blocker/RenderToast';

import i18n from '../../i18n';
import ImageApi from '../../api/Image';
import Home from '../../models/Home';
import Analytics from '../../utils/analytics';
import withAnalytics from '../analytics/withAnalytics';
import { HomeStatusModal } from '../home-status-blocker';
import Transfert from '../../models/Transfert';

const normalizeRotation = (rotation) => ((rotation % 360) + 360) % 360;

const preloadImage = (src) =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = src;
    });

export class UploadList extends React.Component {
    static propTypes = {
        home: PropTypes.instanceOf(Home),
        images: PropTypes.arrayOf(PropTypes.object),
        minImages: PropTypes.number,
        maxImages: PropTypes.number,
        accept: PropTypes.string,
        onChange: PropTypes.func,
        shouldRefreshTopMenuProfile: PropTypes.bool,
        setHome: PropTypes.func,
        fetchHomes: PropTypes.func,
        track: PropTypes.func.isRequired,
        handleSave: PropTypes.func,
        handleTurnOnline: PropTypes.func,
        enableTurnOnlinePopup: PropTypes.bool,
        source: PropTypes.string,
        transferts: PropTypes.arrayOf(PropTypes.instanceOf(Transfert)),
        fetchTransferts: PropTypes.func.isRequired
    };

    static defaultProps = {
        minImages: 5,
        maxImages: 50,
        images: [],
        accept: 'capture=camera, image/jpeg, image/png',
        shouldRefreshTopMenuProfile: true,
        enableTurnOnlinePopup: false
    };

    constructor(props) {
        super(props);
        this.userId = props.home.get('user').get('id');
        this.state = {
            images: this.reindexImages(this.props.images),
            uploading: false,
            showStatusModal: false,
            previousStatus: props.home?.get('status'),
            showToast: false,
            toastText: '',
            newCompletionRate: null,
            previousCompletionRate: props.home?.get('completion_rate'),
            pendingRotations: {}
        };
        this.rotationsInFlight = {};
        this.openDialog = this.openDialog.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.moveCard = this.moveCard.bind(this);
        this.updateOrder = this.updateOrder.bind(this);
        this.uploadFiles = this.uploadFiles.bind(this);
        this.rotateImage = this.rotateImage.bind(this);
        this.deleteImage = this.deleteImage.bind(this);
        this.handleCloseModal = this.handleCloseModal.bind(this);
        this.handleCompletionRateAndStatus = this.handleCompletionRateAndStatus.bind(this);
    }

    componentDidMount() {
        this.props.fetchTransferts();

        // dispatch "upload_list_updated" event
        document.dispatchEvent(
            new CustomEvent('upload_list_updated', {
                detail: {
                    images: this.state.images.length,
                    minImages: this.props.minImages
                }
            })
        );
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.onChange && prevState.images.length != this.state.images.length) {
            this.props.onChange(this.state.images);
        }
        // dispatch "upload_list_updated" event
        document.dispatchEvent(
            new CustomEvent('upload_list_updated', {
                detail: {
                    images: this.state.images.length,
                    minImages: this.props.minImages
                }
            })
        );
        if (prevState.images.length !== this.state.images.length) {
            // Element should be rendered now
            this.updateProgress(this.props.home.get('completion_rate'));
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setState({
            images: this.reindexImages(nextProps.images)
        });
    }

    reindexImages(images) {
        // 0-index the image array based on order display
        if (images && images.length > 0) {
            images = JSON.parse(JSON.stringify(images));

            // Reorder images
            images.sort((a, b) => a.order_display - b.order_display);

            // Reindex from 0
            images.forEach((image, index) => {
                image.order_display = index;
            });
        }

        return images;
    }

    handleChange(e) {
        const { files } = e.target;
        this.uploadFiles(files);
        // reset form and input file value
        e.target.parentNode.reset();
    }

    moveCard(id, hoverIndex) {
        const currentIndex = this.state.images.findIndex((image) => image.id === id);
        // image not found
        if (currentIndex === -1) {
            return false;
        }
        // didn't move
        if (currentIndex === hoverIndex) {
            return false;
        }
        // prevent overflow
        if (hoverIndex >= this.state.images.length) {
            return false;
        }
        const images = JSON.parse(JSON.stringify(this.state.images));
        // moves items
        images.splice(hoverIndex, 0, images.splice(currentIndex, 1)[0]);
        // update items's order_display
        images.forEach((img, index) => {
            img.order_display = index;
        });
        // update display list
        this.setState({
            images
        });

        return true;
    }

    updateOrder() {
        // update images order
        const images = this.state.images.map((img) => _.pick(img, ['id', 'order_display', 'rotate', 'type']));
        ImageApi.sort({ images });
    }

    renderTarget(i) {
        return (
            <ImageDropTarget
                key={i}
                index={i}
                moveCard={this.moveCard}
                updateOrder={this.updateOrder}
                openDialog={this.openDialog}
                uploadFiles={this.uploadFiles}
            >
                {this.renderImage(i)}
            </ImageDropTarget>
        );
    }

    renderImage(i) {
        const image = this.state.images.find((img) => img.order_display == i);
        if (image) {
            return (
                <ImageContainer
                    image={image}
                    pendingRotation={this.state.pendingRotations[image.id]}
                    rotate={this.rotateImage}
                    delete={this.deleteImage}
                />
            );
        }
    }

    uploadFiles(files) {
        const { home, enableTurnOnlinePopup } = this.props;
        const update = [];
        // create promises list with each upload file requests
        for (let i = 0; i < files.length; i++) {
            update.push(ImageApi.post('home', home.id, files[i]));
        }
        this.setState({ uploading: true });
        // sort files when all uploaded and update display list
        return Promise.all(update).then((results) => {
            // iterable tracking
            try {
                Analytics.trackIterable('HomePhotosUpdated', {
                    email: this.props.home.get('user').get('email'),
                    homeId: this.props.home.get('id')
                });
                this.pushGTMTracking(this.props.source);
            } catch (error) {
                console.error('Error while tracking Iterable - HomePhotosUpdated', error);
            }
            this.props.fetchHomes();
            if (this.props.shouldRefreshTopMenuProfile) {
                // Update store with new completion rate
                const completionRate = Math.max(...results.map((result) => result.home.completion_rate));
                if (enableTurnOnlinePopup) {
                    this.handleCompletionRateAndStatus(completionRate, home);
                }

                home.set('completion_rate', completionRate);
                this.props.setHome(home);
                this.setState({
                    previousStatus: home.get('status'),
                    newCompletionRate: completionRate,
                    previousCompletionRate: home.get('completion_rate')
                });
            }
            this.setState(
                (prevState) => {
                    const nextImages = prevState.images.concat(results);
                    nextImages.sort((a, b) => a.order_display - b.order_display);
                    return {
                        images: nextImages,
                        uploading: false
                    };
                },
                () => {
                    const progressPercentage = Math.max(
                        ...results.map((result) => result.home.completion_rate)
                    );
                    this.updateProgress(progressPercentage);
                }
            );
        });
    }

    updateProgress(percentage) {
        $('.panel-filling .progress-filling .progress-bar-success').css('width', `${percentage}%`);
        $(`.filling-user-${this.userId}, .progress-filling span`).text(`${percentage}%`);
    }

    rotateImage(image, rotation) {
        this.setState(
            (prevState) => ({
                pendingRotations: {
                    ...prevState.pendingRotations,
                    [image.id]: (prevState.pendingRotations[image.id] || 0) + rotation
                }
            }),
            () => this.syncRotation(image.id)
        );
    }

    syncRotation(imageId) {
        const pendingRotation = this.state.pendingRotations[imageId] || 0;
        const image = this.state.images.find((img) => img.id == imageId);
        if (this.rotationsInFlight[imageId] || !image || normalizeRotation(pendingRotation) === 0) {
            return;
        }

        this.rotationsInFlight[imageId] = true;
        const rotate = normalizeRotation((image.rotate || 0) + pendingRotation);
        ImageApi.update(imageId, { rotate })
            .then((response) => preloadImage(getImageSrc(response)).then(() => response))
            .then(
                (response) => this.settleRotation(imageId, pendingRotation, response),
                () => this.settleRotation(imageId, pendingRotation)
            );
    }

    settleRotation(imageId, sentRotation, response) {
        this.setState(
            (prevState) => ({
                images: prevState.images.map((img) =>
                    response && img.id == imageId
                        ? { ...img, rotate: response.rotate, cdn_link: response.cdn_link }
                        : img
                ),
                pendingRotations: {
                    ...prevState.pendingRotations,
                    [imageId]: prevState.pendingRotations[imageId] - sentRotation
                }
            }),
            () => {
                this.rotationsInFlight[imageId] = false;
                this.syncRotation(imageId);
            }
        );
        if (response) {
            this.props.fetchHomes();
        }
    }

    deleteImage(image) {
        const promise = ImageApi.delete(image.id);
        promise.then((data) => {
            this.pushGTMTracking(this.props.source);

            this.props.fetchHomes();

            if (this.props.shouldRefreshTopMenuProfile) {
                const { home } = this.props;
                const previousStatus = home.get('status');
                const previousCompletionRate = home.get('completion_rate');

                const completionRate = data.home.completion_rate;
                home.set('completion_rate', completionRate);

                if (completionRate < Home.HOME_COMPLETION_RATE_80) {
                    home.set('status', Home.STATUS_OFFLINE);
                }

                this.props.setHome(home);

                if (this.props.enableTurnOnlinePopup) {
                    this.handleCompletionRateAndStatus(
                        completionRate,
                        home,
                        previousStatus,
                        previousCompletionRate
                    );
                }

                this.setState({ previousStatus });
            }

            this.setState(
                (prevState) => {
                    let images = prevState.images.filter((img) => img.id != image.id);
                    images = this.reindexImages(images);
                    return { images };
                },
                () => {
                    const progressPercentage = data.home.completion_rate;
                    this.updateProgress(progressPercentage);
                }
            );
        });
    }

    hasHomeCompletionTransfert() {
        return this.props.home.get('user')
            ? this.props.transferts?.some((transfert) => {
                  const motive = transfert.get('motive');
                  return motive === Transfert.MOTIVE_FILLING_WRITE_HOME;
              })
            : false;
    }
    openDialog() {
        $(this.inputFile).trigger('click');
    }

    render() {
        if (!this.props.home) {
            return <Loading />;
        }
        let remaining = 0;
        if (this.state.images.length) {
            remaining = this.props.minImages - this.state.images.length;
        }
        // render targets with an empty target if there is less images than maxImages
        const targets = [];
        const imageLength = this.state.images.length;
        const itemsLength = Math.max(
            Math.min(imageLength + 1, Math.max(imageLength, this.props.maxImages)),
            this.props.minImages
        );
        for (let i = 0; i < itemsLength; i++) {
            targets.push(this.renderTarget(i));
        }

        const { home } = this.props;
        const user = home.get('user');
        const isHomeActive = home.get('status') === Home.STATUS_ONLINE;
        const wasOffline = home.get('status') === Home.STATUS_OFFLINE;

        return (
            <section className="upload upload-list">
                {this.state.showToast && <RenderToast toastText={this.state.toastText} />}
                <div className="upload-action">
                    <form className="upload-form text-center" encType="multipart/form-data">
                        <button
                            type="button"
                            className={classNames('btn btn-primary btn-ajax', {
                                sending: this.state.uploading
                            })}
                            onClick={this.openDialog}
                        >
                            <i className="icon icon-Camera camera-icon" aria-hidden="true"></i>
                            <span>{i18n.t('upload:uploadForm_addImages')}</span>
                        </button>
                        <input
                            ref={(input) => {
                                this.inputFile = input;
                            }}
                            id="inputUploadFile"
                            type="file"
                            name="files[]"
                            multiple
                            accept={this.props.accept}
                            onChange={this.handleChange}
                        />
                    </form>
                    {remaining > 0 && (
                        <p className="well-done">
                            {i18n.t('upload:uploadForm_wellDone', { count: remaining })}
                        </p>
                    )}
                </div>
                <div className="row upload-images">{targets}</div>
                <HomeStatusModal
                    isOpen={this.state.showStatusModal}
                    onClose={this.handleCloseModal}
                    home={home}
                    showModalMissingInformation={false}
                    newCompletionRate={this.state.newCompletionRate}
                    homeCompletionRate={this.state.previousCompletionRate}
                    isHomeActive={isHomeActive}
                    wasOffline={wasOffline}
                    turnOnline={this.props.handleTurnOnline}
                    hasHomeCompletionTransfert={this.hasHomeCompletionTransfert()}
                    GPValue={home.get('guestpoint')}
                    isCollection={user.get('is_he_collection')}
                    handleSave={this.props.handleSave}
                    GPGiftOfCompletedHome={Transfert.MOTIVES_VALUE[Transfert.MOTIVE_FILLING_WRITE_HOME]}
                    homeSaved={true}
                />
            </section>
        );
    }

    handleCloseModal() {
        this.setState({ showStatusModal: false });
    }

    pushGTMTracking(source) {
        if (source === 'home-edit') {
            Analytics.trackGTM('HomeEdition', {
                event_data: {
                    action: 'click',
                    text: 'photo_changed',
                    area: 'home edit'
                }
            });
        }
    }

    handleCompletionRateAndStatus(
        completionRate,
        home,
        previousStatus = this.state.previousStatus,
        previousCompletionRate = home.get('completion_rate')
    ) {
        const wasOnlineBefore = previousStatus === Home.STATUS_ONLINE;
        const isOnlineAfter = home.get('status') === Home.STATUS_ONLINE;

        // Automatically set home status to online if completion rate passes 80%
        if (completionRate >= Home.HOME_COMPLETION_RATE_80 && completionRate > previousCompletionRate) {
            home.set('status', Home.STATUS_ONLINE);
            this.props.setHome(home);
            this.setState({
                showStatusModal: true,
                previousStatus: Home.STATUS_ONLINE
            });
            Analytics.trackGTM('MixpanelHomeQualified', {
                userStatus: home.get('user').getStatus()
            });
            return;
        }

        if (
            wasOnlineBefore &&
            isOnlineAfter &&
            home.get('completion_rate') < Home.HOME_COMPLETED &&
            (this.hasHomeCompletionTransfert() || home.get('completion_rate') === previousCompletionRate)
        ) {
            this.setState({
                showToast: true,
                toastText: i18n.t('home:global.toast.updatesSaved.online')
            });
        } else if (
            home.get('completion_rate') < Home.HOME_COMPLETION_RATE_80 ||
            (wasOnlineBefore && !isOnlineAfter)
        ) {
            home.set('status', Home.STATUS_OFFLINE);
            this.props.setHome(home);
            this.setState({
                showToast: true,
                toastText: i18n.t('home:global.toast.updatesSaved.offline')
            });
        }

        // Update previousStatus for next time
        this.setState({
            previousStatus: home.get('status')
        });
    }
}

const mapDispatchToProps = (dispatch) => ({
    setHome: bindActionCreators(setHome, dispatch),
    fetchHomes: bindActionCreators(fetchHomes, dispatch),
    fetchTransferts: bindActionCreators(fetchTransferts, dispatch)
});

const mapStateToProps = (state) => ({
    transferts: state.transferts
});

export const UploadListWithDragAndDrop = withDragDropContext(UploadList);
export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withAnalytics
)(UploadListWithDragAndDrop);
